import React from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import StatusesWrapper from 'sharedComponents/UI/StatusesWrapper';
import CloseIcon from '@mui/icons-material/Close';
import { formatDateAndTime } from 'utilities/time';
import type { SparkRunsStatusType } from 'types/entityTypes/SparkRunsStatusTypes';
import type { SparkRun } from 'types/SparkRun';
import { formatCredits } from 'utilities/index';
import { SparkScrollableGridContainer, SparkTabs } from 'sharedComponents/UI';
import TaskDetailsTab from './TaskDetailsTab';
import { SparkTask } from 'types/SparkTasks';
import useExpandAppContentConfig from 'utilities/styles';
import { SparkSurveyForm } from 'external/SurveyJs';
import { useFormByID } from 'hooks/Forms';
import { Spark } from 'types/Spark/Spark';

const SparkRunDetailItem = ({
  value,
  title,
}: {
  value: string | number;
  title: string;
}) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '18px',
          paddingTop: '50px',
          fontFamily: 'Mont-SemiBold',
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: '16px' }}>{value}</Typography>
    </Box>
  );
};

type SparkRunSideBarProps = {
  status: SparkRunsStatusType | undefined;
  credits: number;
  sparkRun: SparkRun | undefined;
  spark: Spark | undefined;
  open: boolean;
  toggleOpen: () => void;
  statusBarHeight: number;
  sparkTasks: SparkTask[] | undefined;
  sparkRunID: string;
};

function SparkRunSideBar({
  credits,
  status,
  sparkRun,
  spark,
  open,
  toggleOpen,
  statusBarHeight,
  sparkRunID,
  sparkTasks,
}: SparkRunSideBarProps) {
  const { expandAppClasses } = useExpandAppContentConfig();
  const { form } = useFormByID(spark?.attributes.inputFormID);
  return (
    <Drawer
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: '500px',
          borderRight: (theme) => `1px solid ${theme.palette.borderColor}`,
          marginTop: '6rem',
          height: `calc(100vh - 6rem)`,
          transition: (theme) =>
            theme.transitions.create(['margin', 'height'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
      className={expandAppClasses.sidebar}
      variant='persistent'
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {open && (
          <IconButton
            aria-label='close'
            onClick={toggleOpen}
            sx={{
              color: (theme) => theme.palette.primary.main,
              position: 'absolute',
              top: '2rem',
              right: 0,
              transform: 'translate(-50%,-50%)',
              zIndex: 20,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <SparkScrollableGridContainer
        item
        header='Info'
        scrollableContainerStyle={{
          flexDirection: 'column',
          alignContent: 'stretch',
        }}
      >
        <SparkTabs
          tabPanelStyles={{ paddingLeft: 2 }}
          height={`calc(100vh - ${statusBarHeight}px)`}
          tabItems={[
            {
              label: 'Run',
              content: (
                <>
                  {!!status && (
                    <Box display='flex' alignItems='center'>
                      <StatusesWrapper
                        iconUrl={status.attributes.iconURL}
                        name={status.attributes.name}
                        statusColor={status.attributes.textColour}
                      />
                    </Box>
                  )}
                  <SparkRunDetailItem
                    title='Run ID'
                    value={sparkRun?.id || '- -'}
                  />
                  <SparkRunDetailItem
                    title='Start Date'
                    value={
                      formatDateAndTime(sparkRun?.attributes.runStart) || '- -'
                    }
                  />
                  <SparkRunDetailItem
                    title='End Date'
                    value={
                      formatDateAndTime(sparkRun?.attributes.runEnd) || '- -'
                    }
                  />
                  <SparkRunDetailItem
                    title={credits !== 1 ? 'Credits' : 'Credit'}
                    value={formatCredits(sparkRun?.meta.credits) || '0'}
                  />
                </>
              ),
            },
            {
              label: `${sparkTasks?.length || ''} ${
                (sparkTasks?.length || 0) !== 1 ? 'Tasks' : 'Task'
              }`,
              content: <TaskDetailsTab sparkRunID={sparkRunID} />,
            },
            {
              label: 'Input',
              content: (
                <SparkSurveyForm
                  id={sparkRunID}
                  formConfigJSON={form?.attributes.config || ''}
                  height={'100%'}
                  previewMode={true}
                  initialFormValues={sparkRun?.attributes.input}
                  shouldProgressBarRender={false}
                  isReadOnly={true}
                />
              ),
            },
          ]}
        />
      </SparkScrollableGridContainer>
    </Drawer>
  );
}

export default SparkRunSideBar;
